import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { sopService } from '@/services/sop.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/dateFormat';

export function ComplianceDialog({ sopId, sopTitle, onClose }: { sopId: string; sopTitle: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['sop', 'compliance', sopId],
    queryFn: () => sopService.getCompliance(sopId),
  });
  const data = query.data;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] w-full max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Compliance: {sopTitle}</DialogTitle>
        </DialogHeader>

        {query.isLoading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : !data ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No data</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-500/10">
                <p className="text-2xl font-semibold text-emerald-700 dark:text-emerald-400">{data.acknowledged_count}</p>
                <p className="text-xs text-muted-foreground">Acknowledged</p>
              </div>
              <div className="rounded-lg bg-amber-50 p-3 dark:bg-amber-500/10">
                <p className="text-2xl font-semibold text-amber-700 dark:text-amber-400">{data.pending_count}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="rounded-lg bg-rose-50 p-3 dark:bg-rose-500/10">
                <p className="text-2xl font-semibold text-rose-700 dark:text-rose-400">{data.overdue_count}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
            </div>

            <div className="max-h-[48vh] overflow-y-auto rounded-lg border border-border/60">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Employee</th>
                    <th className="px-3 py-2 text-left font-semibold">Department</th>
                    <th className="px-3 py-2 text-left font-semibold">Due</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {data.rows.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No audience yet</td></tr>
                  ) : (
                    data.rows.map((r) => (
                      <tr key={r.user_id} className="hover:bg-muted/30">
                        <td className="px-3 py-2">{r.name}{r.is_mandatory && <span className="ml-1 text-xs text-amber-600">*</span>}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.department_name ?? '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                        <td className="px-3 py-2">
                          {r.acknowledged ? (
                            <Badge variant="success"><CheckCircle2 className="h-3 w-3" /> {formatDate(r.acknowledged_at)}</Badge>
                          ) : r.overdue ? (
                            <Badge variant="destructive"><AlertTriangle className="h-3 w-3" /> Overdue</Badge>
                          ) : (
                            <Badge variant="secondary"><Clock className="h-3 w-3" /> Pending</Badge>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
